import { useRouter } from "next/router";
import type { ReactNode } from "react";
import { createContext, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useErrorHandler } from "react-error-boundary";
import LoadingBar from "react-top-loading-bar";

import { useAuth } from "~/providers/AuthProvider";
import type { UserType } from "~/types/user";
import { homePath, setupPath } from "~/utils/paths";
import { api } from "~/utils/trpc";

export type UserContextProps = {
  user?: UserType | null;
  isLoading: boolean;
};

export const UserContext = createContext<UserContextProps>({} as UserContextProps);

export const UserProvider = ({ children }: { children: ReactNode }) => {
  const { email, isLoading: authLoading } = useAuth();
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const { asPath, replace } = useRouter();
  const handleError = useErrorHandler();

  const { data: user, error } = api.user.getBySession.useQuery(undefined, {
    enabled: !!email,
    refetchOnMount: false,
    refetchOnReconnect: false,
    refetchOnWindowFocus: false,
  });

  // watches auth provider context data and hydrates user data accordingly
  useEffect(() => {
    if (!authLoading) {
      if (!email) {
        // user is not logged in. finished loading
        setIsLoading(false);
      }
    }
  }, [authLoading, email]);

  // handle successful loads
  useEffect(() => {
    // biome-ignore lint/style/noRestrictedGlobals: <explanation>
    if (user && email && typeof window !== "undefined") {
      // biome-ignore lint/style/noRestrictedGlobals: <explanation>
      (window as any).axiomUser = email;

      // force username setup if coming through SSO or if
      // user created account on main website
      const requiresSetup = !user.username;
      const onSetupPage = asPath.includes("/setup");
      const forceSetup = requiresSetup && !onSetupPage;
      if (forceSetup) {
        const root = asPath.includes("/setup") ? homePath : asPath;
        void replace(setupPath(root));
      }
    }

    // done
    setIsLoading(false);
  }, [user, asPath, replace, email]);

  // handle fail loads
  useEffect(() => {
    if (error) {
      // handle sessions expiring
      handleError(error);

      // done
      setIsLoading(false);
    }
  }, [error, handleError]);

  const ref = useRef(null);

  // drive loading bar
  useEffect(() => {
    if (isLoading && !!ref.current) {
      (ref.current as any).continuousStart();
    } else if (!isLoading && !!ref.current) {
      (ref.current as any).complete();
    }
  }, [isLoading]);

  const memoisedValue = useMemo(
    () => ({
      user,
      isLoading,
    }),
    [user, isLoading],
  );

  return (
    <UserContext.Provider value={memoisedValue}>
      <LoadingBar color="#ff7b00" ref={ref} />
      {children}
    </UserContext.Provider>
  );
};

export const useUser = () => useContext(UserContext);
